import { Grid, makeStyles } from '@material-ui/core'
import LegendIcon from 'components/CopilotControl/icons/LegendIcon'
import Tooltip from 'components/common/Tooltip'
import InfoOutlined from 'components/common/icons/InfoOutlined'
import DazzedHeadingForGrid from 'components/common/text/DazzedHeadingForGrid'
import { HygieneTrends, getOralHygieneCategoryColor } from 'consts/oralHygieneReportConsts'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import BaseWidget from '../../components/common/BaseWidget'
import EngagementWidgetSkeleton from '../../components/common/EngagementWidget/EngagementWidgetSkeleton'
import SeePatientsFooter from './SeePatientsFooter'

// Figma frame 218 orders the bars Receding, Stable, Improving (worst to best). Inconsistent sits
// outside that scale and is appended last.
const TREND_BAR_ORDER = [
  HygieneTrends.Receding,
  HygieneTrends.Stable,
  HygieneTrends.Improving,
  HygieneTrends.Inconsistent
]

const useStyles = makeStyles(() => ({
  // Flex-fills the column slot above the number cards; BaseWidget's 5px margin stays inside it.
  root: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  container: {
    position: 'relative',
    flexGrow: 1,
    minHeight: 0,
    overflow: 'hidden',
    padding: 20,
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  tooltip: {
    marginLeft: 6,
    display: 'flex',
    '& svg': {
      width: 14,
      height: 14
    }
  },
  chartContainer: {
    flexGrow: 1,
    minHeight: 90,
    marginTop: 10
  },
  // Four items no longer fit one row in the narrowest column, so the legend wraps instead of clipping.
  legend: {
    flexShrink: 0,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    columnGap: 16,
    rowGap: 4,
    paddingTop: 6
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'Dazzed',
    fontSize: 11,
    fontWeight: 600,
    color: '#6D6D71',
    '& svg': {
      flexShrink: 0
    }
  },
  errorState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  }
}))

/**
 * Value label rendered as a white rounded box overlapping the top of its bar, as in Figma.
 */
const BarValueLabel = ({ x = 0, y = 0, width = 0, value }) => {
  const boxWidth = Math.max(24, String(value).length * 7 + 8)
  const boxHeight = 17
  const boxX = x + width / 2 - boxWidth / 2
  const boxY = y - boxHeight / 2

  return (
    <g>
      <rect x={boxX} y={boxY} width={boxWidth} height={boxHeight} rx={2} fill="#FFFFFF" stroke="#E9E9E9" />
      <text
        x={x + width / 2}
        y={boxY + boxHeight / 2 + 4}
        textAnchor="middle"
        fontFamily="Dazzed"
        fontSize={11}
        fontWeight={500}
        fill="#001335"
      >
        {value}
      </text>
    </g>
  )
}

const HygieneTrendWidget = ({ counts = {}, isLoading, isError, isSelected, onSeePatients }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [hovered, setHovered] = useState(false)

  const hasAnyData = Object.values(counts).some(value => value > 0)

  const { improving = 0, stable = 0, receding = 0, inconsistent = 0 } = counts
  // The chart subtree is memoized on the four counts: recharts restarts (and cuts short) its bar
  // animation whenever it receives new child elements, and this widget re-renders on hover and while
  // the sibling list loads. Reusing the same elements keeps the mount animation running like Copilot's.
  const { barsData, chart } = useMemo(() => {
    const countByTrend = { improving, stable, receding, inconsistent }
    const bars = TREND_BAR_ORDER.map(trend => ({
      key: trend,
      name: t(`pages.oralHygieneReport.patientsList.categories.${trend}`),
      value: countByTrend[trend],
      color: getOralHygieneCategoryColor(trend)
    }))

    return {
      barsData: bars,
      chart: (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bars} margin={{ top: 14, right: 10, left: -20, bottom: 0 }} barCategoryGap="28%">
            <CartesianGrid vertical={false} stroke="#E9ECF5" />
            <XAxis dataKey="name" tick={false} axisLine={false} tickLine={false} />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickCount={5}
              allowDecimals={false}
              tick={{ fontFamily: 'Dazzed', fontSize: 11, fontWeight: 500, fill: '#6D6D71' }}
            />
            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
              {bars.map(bar => (
                <Cell key={bar.key} fill={bar.color} />
              ))}
              <LabelList dataKey="value" content={<BarValueLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )
    }
  }, [improving, stable, receding, inconsistent, t])

  return (
    <BaseWidget className={classes.root} carousel={false} isSelected={isSelected} removePadding>
      <div
        className={classes.container}
        tabIndex={0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={e => !e.currentTarget.contains(e.relatedTarget) && setHovered(false)}
      >
        {isLoading ? (
          <EngagementWidgetSkeleton />
        ) : isError ? (
          <div className={classes.errorState}>{t('general.genericError')}</div>
        ) : (
          <>
            <Grid item className={classes.title}>
              <DazzedHeadingForGrid regular xs={16}>
                {t('pages.oralHygieneReport.widgets.hygieneTrend.title')}
              </DazzedHeadingForGrid>
              <Tooltip
                value={t('pages.oralHygieneReport.widgets.hygieneTrend.tooltip')}
                containerClassName={classes.tooltip}
                placement="top"
                arrow
                variant="light"
              >
                <InfoOutlined color="#BEC6D6" />
              </Tooltip>
            </Grid>
            <div className={classes.chartContainer}>{chart}</div>
            <div className={classes.legend}>
              {barsData.map(bar => (
                <div key={bar.key} className={classes.legendItem}>
                  <LegendIcon color={bar.color} />
                  <span>{bar.name}</span>
                </div>
              ))}
            </div>
          </>
        )}
        {hasAnyData && !isLoading && !isError && <SeePatientsFooter visible={hovered} onClick={onSeePatients} />}
      </div>
    </BaseWidget>
  )
}

export default HygieneTrendWidget
